<script setup lang="ts">
  import GenericDialog from '@/components/dialog/GenericDialog.vue'
  import GICollectionColorField from '@/components/form/GICollectionColorField.vue'
  import TextField from '@/components/form/TextField.vue'
  import { useGIDataSpeckleImport, type ImportErrors } from '@/composables/useGIDataSpeckleImport'
  import { useOpengroundImportProgress } from '@/composables/useOpengroundImportProgress'
  import { useProjectStore } from '@/stores/project'
  import {
    OpengroundToSpeckleForm,
    useOpengroundForm,
    useOpengroundStore,
    useSpeckleAuthStore,
    useSpeckleStore
  } from '@haskoning/vue-speckle'
  import SpeckleLoginNoticeDialog from '@/components/dialog/SpeckleLoginNoticeDialog.vue'
  import { useSpeckleSsoStore } from '@/stores/speckleSso'
  import ProgressBar from '@/components/shared/ProgressBar.vue'
  import { useImportWarnings } from '@/composables/useImportWarnings'

  const opengroundUIStore = useOpengroundUIStore()
  const giDataUploadCollectionStore = useGIDataUploadCollectionStore()
  const { importGIData, importProgress } = useGIDataSpeckleImport()

  const { warningGroups } = useImportWarnings(() => importProgress.value.data?.errors ?? {})
  const projectStore = useProjectStore()
  const opengroundStore = useOpengroundStore()
  const { exportProgress } = storeToRefs(opengroundStore)
  const { showDialog } = storeToRefs(opengroundUIStore)
  const { currentItem: currentProject } = storeToRefs(projectStore)
  const speckleStore = useSpeckleStore()
  const speckleAuthStore = useSpeckleAuthStore()
  const speckleSsoStore = useSpeckleSsoStore()
  const { ssoReauthenticationRequired, canUseSpeckle } = storeToRefs(speckleSsoStore)

  watch(showDialog, (open) => {
    if (!open) {
      importProgress.value = { percent: null, message: '', data: {} }
    }
  })

  function closeDialog() {
    importError.value = ''
    opengroundUIStore.showDialog = false
  }

  const redirectUrl = computed(() => {
    const currentUrl = new URL(window.location.href)
    currentUrl.hash = ''
    return currentUrl.toString()
  })

  const opengroundForm = useOpengroundForm()
  const alsoImportToGeoProfile = ref(true)
  const giDataCollectionName = ref('')
  const giDataCollectionColor = ref('#000000')
  const isImporting = ref(false)
  const importError = ref('')
  const isImportDisabled = computed(
    () =>
      isImporting.value ||
      !opengroundForm.isComplete.value ||
      !opengroundStore.opengroundAccessToken ||
      !speckleAuthStore.token ||
      (alsoImportToGeoProfile.value && !giDataCollectionName.value)
  )

  const {
    currentStage,
    exportPercent,
    importPercent,
    importIndeterminate,
    exportMessage,
    importMessage
  } = useOpengroundImportProgress(exportProgress, importProgress)

  async function handleImport() {
    if (isImporting.value) {
      return
    }
    isImporting.value = true
    currentStage.value = 1
    importError.value = ''
    importProgress.value = { percent: null, message: '', data: {} }
    let warnings: ImportErrors = {}

    try {
      const exportResponse = await opengroundStore.exportFormToSpeckle(opengroundForm)
      if (!exportResponse) {
        return
      }

      if (alsoImportToGeoProfile.value) {
        const speckleToken = speckleAuthStore.token
        const speckleProjectId = speckleStore.currentSelection.project?.id
        const speckleModelId = speckleStore.currentSelection.model?.id
        const projectId = currentProject.value?.id

        if (
          !giDataCollectionName.value ||
          !speckleToken ||
          !speckleProjectId ||
          !speckleModelId ||
          !projectId
        ) {
          return
        }

        currentStage.value = 2
        const result = await importGIData({
          speckle_token: speckleToken,
          speckle_project_id: speckleProjectId,
          speckle_model_id: speckleModelId,
          speckle_version_id: exportResponse.speckle_commit_id,
          project: projectId,
          description: `Imported from Speckle with id ${speckleProjectId}`,
          name: giDataCollectionName.value,
          collection_color: giDataCollectionColor.value
        })
        warnings = result?.errors ?? {}
      }

      if (speckleStore.currentSelection.project && speckleStore.currentSelection.model) {
        await speckleStore.getSpeckleVersions()
      }

      closeDialog()
      if (Object.keys(warnings).length) {
        giDataUploadCollectionStore.showImportWarnings(warnings)
      }
    } catch (error) {
      importError.value = error instanceof Error ? error.message : 'Import failed'
    } finally {
      isImporting.value = false
      currentStage.value = 0
    }
  }

  async function opengroundLogin() {
    const loginUrl = await opengroundStore.generateLoginUrl(redirectUrl.value)
    if (loginUrl) {
      window.location.href = loginUrl
    }
  }
</script>

<template>
  <SpeckleLoginNoticeDialog v-if="!canUseSpeckle" v-model="opengroundUIStore.showDialog">
    <template #default v-if="!ssoReauthenticationRequired">
      <GeneralText class="pb-2 d-block">
        Before first use of the connection between OpenGround and GeoProfile the user must request
        access to Speckle:
      </GeneralText>
      <ul class="pl-8 pb-2">
        <li>
          Speckle - Request access via
          <a
            href="https://royalhaskoningdhv-amc.ivanticloud.com/Modules/SelfService/#serviceCatalog/request/6215C7ADD8CB4705BAD0EDC04E8F9E77"
            target="_blank"
            rel="noopener noreferrer"
          >
            Speckle Manager / Service Catalogue / WeConnect</a
          >
        </li>
      </ul>
      <GeneralText class="pb-2 d-block">
        For a new project the user will need to request a Speckle project. This request should be
        made by email or teams message to:
      </GeneralText>
      <ul class="pl-8 pb-2">
        <li>
          <a href="mailto:jelle.van.der.zon@haskoning.com">jelle.van.der.zon@haskoning.com</a> and
        </li>
        <li>
          <a href="mailto:neethu.ragi.manoj@haskoning.com">neethu.ragi.manoj@haskoning.com</a>
        </li>
      </ul>
      <GeneralText class="pb-2 d-block">
        Once the user has confirmation that their project has been created on Speckle then
        connection may be used via this button.
      </GeneralText>
      <GeneralText class="pb-2 d-block">
        For further information on the connection please refer to the
        <a href="/docs/" rel="noopener noreferrer">documentation</a>
      </GeneralText>
      <GeneralText class="d-block">
        If you have completed the actions above, select Continue below.
      </GeneralText>
    </template>
  </SpeckleLoginNoticeDialog>
  <GenericDialog
    :model-value="showDialog"
    v-else-if="!opengroundStore.isLoggedIn"
    @clicked-outside="closeDialog"
  >
    <template #title>Openground Login Required</template>
    <template #default>
      <GeneralText class="pb-2"> To continue you need to login into Openground first </GeneralText>
    </template>
    <template #actions>
      <v-btn variant="text" color="primary" @click="closeDialog"> Cancel </v-btn>
      <v-btn variant="text" color="primary" @click="opengroundLogin"> Continue </v-btn>
    </template>
  </GenericDialog>
  <GenericDialog :model-value="showDialog" v-else @clicked-outside="closeDialog">
    <template #title>OG2Speckle</template>
    <template #default>
      <fieldset
        :disabled="isImporting"
        class="openground-form-fieldset"
        :class="{ 'openground-form-fieldset--disabled': isImporting }"
      >
        <OpengroundToSpeckleForm :form="opengroundForm">
          <template #version-message-label> Speckle Import Message </template>
        </OpengroundToSpeckleForm>
      </fieldset>
      <v-checkbox
        v-model="alsoImportToGeoProfile"
        label="Also import to GeoProfile"
        color="primary"
        class="mt-2"
        hide-details
        :disabled="isImporting"
      />
      <template v-if="alsoImportToGeoProfile">
        <TextField
          v-model="giDataCollectionName"
          label="Name of GI Data Collection"
          :rules="[(v: string) => !!v || 'Name is required']"
          :disabled="isImporting"
        />
        <GICollectionColorField
          v-model="giDataCollectionColor"
          class="mt-2"
          :disabled="isImporting"
        />
      </template>
      <div v-if="isImporting" class="mt-4">
        <ProgressBar :percent="exportPercent" :message="exportMessage" />
        <ProgressBar
          v-if="alsoImportToGeoProfile"
          class="mt-3"
          :percent="importIndeterminate ? null : importPercent"
          :indeterminate="importIndeterminate"
          :message="importMessage"
        />
      </div>
      <v-alert v-if="warningGroups.length > 0" type="warning" variant="tonal" class="mt-4">
        <p class="mb-1">Some locations were skipped during import:</p>
        <div v-for="group in warningGroups" :key="group.code" class="mt-3">
          <p class="mb-1">{{ group.summary }}</p>
          <p class="ml-6 mb-0" style="max-height: 30vh; overflow-y: auto">
            {{ group.detail }}
          </p>
        </div>
      </v-alert>
      <v-alert v-if="importError" type="error" variant="flat" class="mt-4">
        {{ importError }}
      </v-alert>
    </template>
    <template #actions>
      <v-btn variant="text" color="primary" @click="closeDialog"> Cancel </v-btn>
      <v-btn
        variant="text"
        color="primary"
        :loading="isImporting"
        :disabled="isImportDisabled"
        @click="handleImport"
      >
        Import
      </v-btn>
    </template>
  </GenericDialog>
</template>

<style scoped>
  .openground-form-fieldset {
    border: 0;
    padding: 0;
    margin: 0;
    min-inline-size: auto;
  }

  .openground-form-fieldset--disabled {
    opacity: 0.6;
    pointer-events: none;
  }
</style>
